import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { api } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { ScrollArea } from '../ui/scroll-area';
import { Separator } from '../ui/separator';
import {
  Clock, Plus, Trash2, Save, Edit3, Coffee, Briefcase,
  Shield, CalendarDays, ChevronDown, Copy, AlertCircle, Check
} from 'lucide-react';
import { toast } from 'sonner';

const DAYS = [
  { value: 1, label: 'Monday', short: 'Mon' },
  { value: 2, label: 'Tuesday', short: 'Tue' },
  { value: 3, label: 'Wednesday', short: 'Wed' },
  { value: 4, label: 'Thursday', short: 'Thu' },
  { value: 5, label: 'Friday', short: 'Fri' },
  { value: 6, label: 'Saturday', short: 'Sat' },
  { value: 7, label: 'Sunday', short: 'Sun' },
];

const BLOCK_TYPES = [
  { value: 'available', label: 'Available', color: '#3B82F6', bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700', icon: Clock },
  { value: 'lunch', label: 'Lunch', color: '#F59E0B', bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', icon: Coffee },
  { value: 'admin', label: 'Admin Time', color: '#8B5CF6', bg: 'bg-violet-50', border: 'border-violet-200', text: 'text-violet-700', icon: Briefcase },
  { value: 'meeting', label: 'Meeting', color: '#0D9488', bg: 'bg-teal-50', border: 'border-teal-200', text: 'text-teal-700', icon: CalendarDays },
  { value: 'off', label: 'Off / Unavailable', color: '#6B7280', bg: 'bg-gray-50', border: 'border-gray-200', text: 'text-gray-500', icon: Shield },
];

const HOURS = Array.from({ length: 13 }, (_, i) => i + 6); // 6am - 6pm
const TIME_OPTIONS = [];
for (let h = 6; h <= 20; h++) {
  for (let m = 0; m < 60; m += 15) {
    const val = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
    const ampm = h >= 12 ? 'PM' : 'AM';
    const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
    TIME_OPTIONS.push({ value: val, label: `${h12}:${String(m).padStart(2, '0')} ${ampm}` });
  }
}

function timeToMinutes(t) {
  if (!t) return 0;
  const [h, m] = t.split(':').map(Number);
  return h * 60 + m;
}

function minutesToTime(mins) {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

function formatTime12(t) {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
}

function getBlockStyle(blockType) {
  return BLOCK_TYPES.find(bt => bt.value === blockType) || BLOCK_TYPES[4];
}

// ============================================================
// BLOCK BAR - Visual representation of a time block on the grid
// ============================================================

function BlockBar({ block, gridStart, gridEnd, onEdit, onDelete }) {
  const style = getBlockStyle(block.block_type);
  const Icon = style.icon;
  const startMin = timeToMinutes(block.start_time);
  const endMin = timeToMinutes(block.end_time);
  const gridStartMin = gridStart * 60;
  const gridEndMin = gridEnd * 60;
  const totalGridMin = gridEndMin - gridStartMin;

  const leftPct = ((startMin - gridStartMin) / totalGridMin) * 100;
  const widthPct = ((endMin - startMin) / totalGridMin) * 100;

  if (leftPct < 0 || leftPct >= 100) return null;

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            className={`absolute top-0.5 bottom-0.5 rounded-md ${style.bg} ${style.border} border cursor-pointer
              flex items-center gap-1 px-1.5 overflow-hidden group
              transition-shadow duration-150 hover:shadow-md hover:z-10`}
            style={{ left: `${leftPct}%`, width: `${Math.max(widthPct, 2)}%` }}
            onClick={() => onEdit(block)}
            data-testid={`block-bar-${block.block_type}-${block.day_of_week}`}
          >
            <Icon className={`w-3 h-3 ${style.text} shrink-0`} />
            {widthPct > 8 && (
              <span className={`text-[10px] font-medium ${style.text} truncate`}>
                {block.label || style.label}
              </span>
            )}
            <button
              className="absolute top-0 right-0 p-0.5 opacity-0 group-hover:opacity-100 transition-opacity
                bg-white/80 rounded-bl-md"
              onClick={(e) => { e.stopPropagation(); onDelete(block); }}
              data-testid={`block-delete-${block.block_type}-${block.day_of_week}`}
            >
              <Trash2 className="w-2.5 h-2.5 text-destructive" />
            </button>
          </div>
        </TooltipTrigger>
        <TooltipContent side="top" className="text-xs">
          <p className="font-medium">{block.label || style.label}</p>
          <p className="text-muted-foreground">{formatTime12(block.start_time)} - {formatTime12(block.end_time)}</p>
          {block.block_type === 'available' && block.slot_duration_minutes && (
            <p className="text-muted-foreground">{block.slot_duration_minutes}-min slots</p>
          )}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

// ============================================================
// BLOCK EDIT DIALOG
// ============================================================

function BlockEditDialog({ open, onClose, block, onSave, isNew }) {
  const [form, setForm] = useState({
    block_type: 'available',
    start_time: '08:00',
    end_time: '12:00',
    label: '',
    slot_duration_minutes: 15,
    day_of_week: 1,
  });

  useEffect(() => {
    if (block) {
      setForm({
        block_type: block.block_type || 'available',
        start_time: block.start_time || '08:00',
        end_time: block.end_time || '12:00',
        label: block.label || '',
        slot_duration_minutes: block.slot_duration_minutes || 15,
        day_of_week: block.day_of_week || 1,
      });
    }
  }, [block]);

  const handleSave = () => {
    if (timeToMinutes(form.end_time) <= timeToMinutes(form.start_time)) {
      toast.error('End time must be after start time');
      return;
    }
    onSave({
      ...form,
      slot_duration_minutes: form.block_type === 'available' ? (form.slot_duration_minutes || 15) : null,
    });
  };

  const style = getBlockStyle(form.block_type);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" data-testid="block-edit-dialog">
        <DialogHeader>
          <DialogTitle className="text-base flex items-center gap-2" style={{ fontFamily: 'Space Grotesk' }}>
            {isNew ? <Plus className="w-4 h-4" /> : <Edit3 className="w-4 h-4" />}
            {isNew ? 'Add Schedule Block' : 'Edit Schedule Block'}
          </DialogTitle>
          <DialogDescription className="text-xs">
            Configure the time block for this provider's schedule.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Block Type */}
          <div className="space-y-1.5">
            <Label className="text-xs font-medium">Block Type</Label>
            <div className="grid grid-cols-3 gap-1.5">
              {BLOCK_TYPES.map(bt => {
                const BtIcon = bt.icon;
                return (
                  <button
                    key={bt.value}
                    className={`flex items-center gap-1.5 px-2.5 py-2 rounded-lg border text-xs font-medium transition-colors
                      ${form.block_type === bt.value
                        ? `${bt.bg} ${bt.border} ${bt.text} ring-1 ring-offset-1`
                        : 'bg-background border-border text-muted-foreground hover:bg-accent'
                      }`}
                    style={form.block_type === bt.value ? { ringColor: bt.color } : {}}
                    onClick={() => setForm(f => ({ ...f, block_type: bt.value, label: bt.label }))}
                    data-testid={`block-type-${bt.value}`}
                  >
                    <BtIcon className="w-3.5 h-3.5" />
                    {bt.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Day of Week (only if new) */}
          {isNew && (
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Day</Label>
              <Select value={String(form.day_of_week)} onValueChange={v => setForm(f => ({ ...f, day_of_week: parseInt(v) }))}>
                <SelectTrigger className="h-8 text-xs" data-testid="block-day-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DAYS.map(d => (
                    <SelectItem key={d.value} value={String(d.value)} className="text-xs">{d.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Time Range */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Start Time</Label>
              <Select value={form.start_time} onValueChange={v => setForm(f => ({ ...f, start_time: v }))}>
                <SelectTrigger className="h-8 text-xs tabular-nums" data-testid="block-start-time">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="max-h-48">
                  {TIME_OPTIONS.map(t => (
                    <SelectItem key={t.value} value={t.value} className="text-xs tabular-nums">{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">End Time</Label>
              <Select value={form.end_time} onValueChange={v => setForm(f => ({ ...f, end_time: v }))}>
                <SelectTrigger className="h-8 text-xs tabular-nums" data-testid="block-end-time">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="max-h-48">
                  {TIME_OPTIONS.map(t => (
                    <SelectItem key={t.value} value={t.value} className="text-xs tabular-nums">{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Label */}
          <div className="space-y-1.5">
            <Label className="text-xs font-medium">Label</Label>
            <Input
              className="h-8 text-xs"
              value={form.label}
              onChange={e => setForm(f => ({ ...f, label: e.target.value }))}
              placeholder="e.g. Morning Clinic, Team Meeting..."
              data-testid="block-label-input"
            />
          </div>

          {/* Slot Duration (only for available blocks) */}
          {form.block_type === 'available' && (
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Slot Duration</Label>
              <Select value={String(form.slot_duration_minutes || 15)} onValueChange={v => setForm(f => ({ ...f, slot_duration_minutes: parseInt(v) }))}>
                <SelectTrigger className="h-8 text-xs" data-testid="block-slot-duration">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="15" className="text-xs">15 minutes</SelectItem>
                  <SelectItem value="30" className="text-xs">30 minutes</SelectItem>
                  <SelectItem value="45" className="text-xs">45 minutes</SelectItem>
                  <SelectItem value="60" className="text-xs">60 minutes</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-[10px] text-muted-foreground">How the available block is divided for booking</p>
            </div>
          )}

          {/* Preview */}
          <div className="rounded-lg border p-3 bg-muted/30">
            <p className="text-[10px] text-muted-foreground mb-1.5 uppercase tracking-wide font-medium">Preview</p>
            <div className="flex items-center gap-2">
              <div className={`w-full rounded-md ${style.bg} ${style.border} border px-3 py-2 flex items-center gap-2`}>
                {React.createElement(style.icon, { className: `w-3.5 h-3.5 ${style.text}` })}
                <span className={`text-xs font-medium ${style.text}`}>{form.label || style.label}</span>
                <span className={`text-[10px] ${style.text} ml-auto tabular-nums`}>
                  {formatTime12(form.start_time)} - {formatTime12(form.end_time)}
                </span>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" size="sm" onClick={() => onClose(false)} data-testid="block-dialog-cancel">
            Cancel
          </Button>
          <Button size="sm" onClick={handleSave} data-testid="block-dialog-save">
            <Check className="w-3.5 h-3.5 mr-1" />
            {isNew ? 'Add Block' : 'Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================
// COPY DAY DIALOG
// ============================================================

function CopyDayDialog({ open, onClose, fromDay, onCopy }) {
  const [targetDays, setTargetDays] = useState([]);

  const toggleDay = (dayVal) => {
    setTargetDays(prev =>
      prev.includes(dayVal) ? prev.filter(d => d !== dayVal) : [...prev, dayVal]
    );
  };

  const handleCopy = () => {
    if (targetDays.length === 0) {
      toast.error('Select at least one target day');
      return;
    }
    onCopy(fromDay, targetDays);
    setTargetDays([]);
    onClose(false);
  };

  const fromLabel = DAYS.find(d => d.value === fromDay)?.label || '';

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-sm" data-testid="copy-day-dialog">
        <DialogHeader>
          <DialogTitle className="text-base flex items-center gap-2" style={{ fontFamily: 'Space Grotesk' }}>
            <Copy className="w-4 h-4" /> Copy Schedule
          </DialogTitle>
          <DialogDescription className="text-xs">
            Copy {fromLabel}'s schedule to other days.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 py-2">
          {DAYS.filter(d => d.value !== fromDay).map(d => (
            <button
              key={d.value}
              className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg border text-xs transition-colors
                ${targetDays.includes(d.value)
                  ? 'bg-primary/10 border-primary/30 text-primary font-medium'
                  : 'bg-background border-border text-foreground hover:bg-accent'
                }`}
              onClick={() => toggleDay(d.value)}
              data-testid={`copy-target-${d.short.toLowerCase()}`}
            >
              <div className={`w-4 h-4 rounded border flex items-center justify-center
                ${targetDays.includes(d.value) ? 'bg-primary border-primary' : 'border-border'}`}>
                {targetDays.includes(d.value) && <Check className="w-3 h-3 text-primary-foreground" />}
              </div>
              {d.label}
            </button>
          ))}
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" size="sm" onClick={() => onClose(false)}>Cancel</Button>
          <Button size="sm" onClick={handleCopy} disabled={targetDays.length === 0}>
            <Copy className="w-3.5 h-3.5 mr-1" />
            Copy to {targetDays.length} day{targetDays.length !== 1 ? 's' : ''}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================
// MAIN EDITOR
// ============================================================

export default function ScheduleTemplateEditor({ providers }) {
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [template, setTemplate] = useState(null);
  const [localBlocks, setLocalBlocks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [editDialog, setEditDialog] = useState({ open: false, block: null, isNew: false });
  const [copyDialog, setCopyDialog] = useState({ open: false, fromDay: 1 });

  const gridStart = 6;
  const gridEnd = 19;

  // Fetch template for selected provider
  const fetchTemplate = useCallback(async (providerId) => {
    if (!providerId) return;
    setLoading(true);
    try {
      const res = await api.get(`/schedule-templates/${providerId}`);
      const tmplData = res.data.template || res.data.schedule_template;
      setTemplate(tmplData);
      setLocalBlocks(tmplData?.blocks || []);
      setDirty(false);
    } catch (err) {
      if (err.response?.status === 404) {
        setTemplate(null);
        setLocalBlocks([]);
      } else {
        toast.error('Failed to load schedule template');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (providers?.length > 0 && !selectedProvider) {
      setSelectedProvider(providers[0].id);
    }
  }, [providers, selectedProvider]);

  useEffect(() => {
    if (selectedProvider) {
      fetchTemplate(selectedProvider);
    }
  }, [selectedProvider, fetchTemplate]);

  const selectedProv = useMemo(() =>
    providers?.find(p => p.id === selectedProvider),
    [providers, selectedProvider]
  );

  // Block operations
  const addBlock = (newBlock) => {
    setLocalBlocks(prev => [...prev, newBlock]);
    setDirty(true);
    setEditDialog({ open: false, block: null, isNew: false });
    toast.success('Block added');
  };

  const updateBlock = (updatedBlock) => {
    setLocalBlocks(prev => prev.map(b =>
      (b.day_of_week === editDialog.block.day_of_week &&
       b.start_time === editDialog.block.start_time &&
       b.block_type === editDialog.block.block_type)
        ? { ...updatedBlock, day_of_week: editDialog.block.day_of_week }
        : b
    ));
    setDirty(true);
    setEditDialog({ open: false, block: null, isNew: false });
    toast.success('Block updated');
  };

  const deleteBlock = (block) => {
    setLocalBlocks(prev => prev.filter(b =>
      !(b.day_of_week === block.day_of_week &&
        b.start_time === block.start_time &&
        b.block_type === block.block_type)
    ));
    setDirty(true);
    toast.success('Block removed');
  };

  const copyDayBlocks = (fromDay, targetDays) => {
    const sourceBlocks = localBlocks.filter(b => b.day_of_week === fromDay);
    if (sourceBlocks.length === 0) {
      toast.error('No blocks to copy from this day');
      return;
    }
    setLocalBlocks(prev => {
      let updated = prev.filter(b => !targetDays.includes(b.day_of_week));
      for (const targetDay of targetDays) {
        const copied = sourceBlocks.map(b => ({ ...b, day_of_week: targetDay }));
        updated = [...updated, ...copied];
      }
      return updated;
    });
    setDirty(true);
    toast.success(`Copied to ${targetDays.length} day${targetDays.length !== 1 ? 's' : ''}`);
  };

  const handleSave = async () => {
    if (!selectedProvider) return;
    setSaving(true);
    try {
      await api.put(`/schedule-templates/${selectedProvider}`, { blocks: localBlocks });
      setDirty(false);
      toast.success('Schedule template saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const handleAddBlock = (dayOfWeek) => {
    setEditDialog({
      open: true,
      isNew: true,
      block: {
        block_type: 'available',
        start_time: '08:00',
        end_time: '12:00',
        label: 'Available',
        slot_duration_minutes: 15,
        day_of_week: dayOfWeek,
      },
    });
  };

  const handleEditBlock = (block) => {
    setEditDialog({ open: true, isNew: false, block });
  };

  const handleDialogSave = (formData) => {
    if (editDialog.isNew) {
      addBlock(formData);
    } else {
      updateBlock(formData);
    }
  };

  // Summary stats
  const totalAvailableHours = useMemo(() => {
    const avail = localBlocks.filter(b => b.block_type === 'available');
    const totalMin = avail.reduce((sum, b) => sum + (timeToMinutes(b.end_time) - timeToMinutes(b.start_time)), 0);
    return (totalMin / 60).toFixed(1);
  }, [localBlocks]);

  const blocksByDay = useMemo(() => {
    const map = {};
    DAYS.forEach(d => { map[d.value] = []; });
    localBlocks.forEach(b => {
      if (map[b.day_of_week]) {
        map[b.day_of_week].push(b);
      }
    });
    Object.keys(map).forEach(k => {
      map[k].sort((a, b) => timeToMinutes(a.start_time) - timeToMinutes(b.start_time));
    });
    return map;
  }, [localBlocks]);

  return (
    <div className="space-y-4" data-testid="schedule-template-editor">
      {/* Provider Selector + Actions */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex-1 min-w-[200px]">
          <Select value={selectedProvider || ''} onValueChange={setSelectedProvider}>
            <SelectTrigger className="h-9 text-sm" data-testid="template-provider-select">
              <SelectValue placeholder="Select provider..." />
            </SelectTrigger>
            <SelectContent>
              {providers?.map(p => (
                <SelectItem key={p.id} value={p.id} className="text-sm">
                  <span className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: p.color }} />
                    {p.name}
                    <span className="text-xs text-muted-foreground ml-1">{p.specialty}</span>
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {dirty && (
          <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-200 animate-pulse">
            <AlertCircle className="w-3 h-3 mr-1" /> Unsaved changes
          </Badge>
        )}

        <Button
          size="sm"
          onClick={handleSave}
          disabled={saving || !dirty}
          className="gap-1"
          data-testid="template-save-button"
        >
          <Save className="w-3.5 h-3.5" />
          {saving ? 'Saving...' : 'Save Template'}
        </Button>
      </div>

      {/* Summary Stats */}
      {selectedProvider && !loading && (
        <div className="flex gap-3 flex-wrap">
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Clock className="w-3.5 h-3.5" />
            <span className="tabular-nums font-medium">{totalAvailableHours}h</span> available/week
          </div>
          <Separator orientation="vertical" className="h-4" />
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <span className="tabular-nums font-medium">{localBlocks.length}</span> blocks total
          </div>
          {BLOCK_TYPES.filter(bt => localBlocks.some(b => b.block_type === bt.value)).map(bt => {
            const count = localBlocks.filter(b => b.block_type === bt.value).length;
            return (
              <Badge key={bt.value} variant="outline" className={`text-[10px] ${bt.bg} ${bt.text} ${bt.border}`}>
                {count} {bt.label}
              </Badge>
            );
          })}
        </div>
      )}

      {/* Visual Grid */}
      {loading ? (
        <div className="space-y-2">
          {[1, 2, 3, 4, 5].map(i => (
            <div key={i} className="h-10 rounded-lg bg-muted animate-pulse" />
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="p-0">
            {/* Time header */}
            <div className="flex border-b">
              <div className="w-24 shrink-0 p-2 text-[10px] text-muted-foreground font-medium uppercase tracking-wide flex items-center">
                Day
              </div>
              <div className="flex-1 relative flex">
                {HOURS.filter(h => h >= gridStart && h < gridEnd).map(h => {
                  const ampm = h >= 12 ? 'PM' : 'AM';
                  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
                  const pct = ((h - gridStart) / (gridEnd - gridStart)) * 100;
                  return (
                    <div
                      key={h}
                      className="absolute top-0 bottom-0 border-l border-border/40"
                      style={{ left: `${pct}%` }}
                    >
                      <span className="text-[9px] text-muted-foreground tabular-nums pl-0.5 select-none">
                        {h12}{ampm}
                      </span>
                    </div>
                  );
                })}
              </div>
              <div className="w-20 shrink-0" />
            </div>

            {/* Day rows */}
            {DAYS.map(day => {
              const dayBlks = blocksByDay[day.value] || [];
              const hasBlocks = dayBlks.length > 0;

              return (
                <div
                  key={day.value}
                  className={`flex border-b last:border-0 transition-colors
                    ${hasBlocks ? 'bg-background' : 'bg-muted/20'}
                    hover:bg-accent/30`}
                  data-testid={`template-day-row-${day.short.toLowerCase()}`}
                >
                  {/* Day label */}
                  <div className="w-24 shrink-0 p-2 flex flex-col justify-center">
                    <span className="text-xs font-medium">{day.short}</span>
                    <span className="text-[10px] text-muted-foreground">
                      {dayBlks.length > 0 ? `${dayBlks.length} block${dayBlks.length > 1 ? 's' : ''}` : 'No blocks'}
                    </span>
                  </div>

                  {/* Grid area */}
                  <div
                    className="flex-1 relative h-10 cursor-pointer"
                    onClick={(e) => {
                      if (e.target === e.currentTarget) handleAddBlock(day.value);
                    }}
                    data-testid={`template-grid-${day.short.toLowerCase()}`}
                  >
                    {/* Hour lines */}
                    {HOURS.filter(h => h >= gridStart && h < gridEnd).map(h => {
                      const pct = ((h - gridStart) / (gridEnd - gridStart)) * 100;
                      return (
                        <div
                          key={h}
                          className="absolute top-0 bottom-0 border-l border-border/20"
                          style={{ left: `${pct}%` }}
                        />
                      );
                    })}
                    {/* Blocks */}
                    {dayBlks.map((block, idx) => (
                      <BlockBar
                        key={`${block.start_time}-${block.block_type}-${idx}`}
                        block={block}
                        gridStart={gridStart}
                        gridEnd={gridEnd}
                        onEdit={handleEditBlock}
                        onDelete={deleteBlock}
                      />
                    ))}
                  </div>

                  {/* Actions */}
                  <div className="w-20 shrink-0 flex items-center justify-end gap-0.5 pr-2">
                    <TooltipProvider delayDuration={300}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6"
                            onClick={() => handleAddBlock(day.value)}
                            data-testid={`add-block-${day.short.toLowerCase()}`}
                          >
                            <Plus className="w-3.5 h-3.5" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent side="left" className="text-xs">Add block</TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                    {dayBlks.length > 0 && (
                      <TooltipProvider delayDuration={300}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6"
                              onClick={() => setCopyDialog({ open: true, fromDay: day.value })}
                              data-testid={`copy-day-${day.short.toLowerCase()}`}
                            >
                              <Copy className="w-3 h-3" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent side="left" className="text-xs">Copy to other days</TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}

      {/* Block Legend */}
      <div className="flex flex-wrap gap-3">
        {BLOCK_TYPES.map(bt => (
          <div key={bt.value} className="flex items-center gap-1.5">
            <div className={`w-3 h-3 rounded-sm ${bt.bg} ${bt.border} border`} />
            <span className="text-[10px] text-muted-foreground">{bt.label}</span>
          </div>
        ))}
        <div className="flex items-center gap-1.5 ml-auto">
          <span className="text-[10px] text-muted-foreground">Click empty area to add</span>
          <span className="text-[10px] text-muted-foreground">|</span>
          <span className="text-[10px] text-muted-foreground">Click block to edit</span>
        </div>
      </div>

      {/* Dialogs */}
      <BlockEditDialog
        open={editDialog.open}
        onClose={(v) => setEditDialog({ ...editDialog, open: v })}
        block={editDialog.block}
        onSave={handleDialogSave}
        isNew={editDialog.isNew}
      />
      <CopyDayDialog
        open={copyDialog.open}
        onClose={(v) => setCopyDialog({ ...copyDialog, open: v })}
        fromDay={copyDialog.fromDay}
        onCopy={copyDayBlocks}
      />
    </div>
  );
}
